/**
 * One-to-one voice calls in the browser.
 *
 * Audio flows peer-to-peer over WebRTC (DTLS-SRTP encrypted). The server only relays
 * signalling (offer, answer, ICE candidates) and call state over the existing WebSocket.
 * Nothing is recorded: no MediaRecorder is used and no audio is stored anywhere.
 */
import type { CallEndReason, CallPeer, CallSignal, ServerMsg } from "@samra/shared";
import { get, send, subscribe } from "./api";
import { useSyncExternalStore } from "react";

export type CallPhase = "calling" | "ringing" | "incoming" | "connecting" | "active" | "reconnecting" | "ended";

export interface CallState {
  callId: string;
  role: "caller" | "callee";
  peer: CallPeer;
  phase: CallPhase;
  muted: boolean;
  peerMuted: boolean;
  startedAt: number | null;
  endReason: CallEndReason | null;
  error: string | null;
}

let state: CallState | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((f) => f());
export const getCall = () => state;
/** React hook: the current call (or null), re-rendered on every change. */
export function useCall(): CallState | null {
  return useSyncExternalStore(subscribeCall, getCall);
}
export function subscribeCall(f: () => void) {
  listeners.add(f);
  return () => listeners.delete(f);
}
const set = (p: Partial<CallState> | null) => {
  state = p === null ? null : state ? { ...state, ...p } : (p as CallState);
  emit();
};

let pc: RTCPeerConnection | null = null;
let local: MediaStream | null = null;
let remoteAudio: HTMLAudioElement | null = null;
let pendingCandidates: RTCIceCandidateInit[] = [];
let remoteSet = false;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let iceCache: { servers: RTCIceServer[]; until: number } | null = null;

const uuid = () => crypto.randomUUID();

/** Browsers only expose the microphone on secure origins. */
function preflight(): string | null {
  if (!window.isSecureContext) return "requires_https";
  if (!navigator.mediaDevices?.getUserMedia || typeof RTCPeerConnection === "undefined") return "unsupported_browser";
  return null;
}

async function getMicrophone(): Promise<MediaStream> {
  const pre = preflight();
  if (pre) throw Object.assign(new Error(pre), { code: pre });
  try {
    return await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true }, video: false });
  } catch (e: any) {
    const code = e?.name === "NotAllowedError" || e?.name === "SecurityError" ? "mic_permission_denied" : e?.name === "NotFoundError" || e?.name === "OverconstrainedError" ? "no_microphone" : "mic_unavailable";
    throw Object.assign(new Error(code), { code });
  }
}

async function iceServers(): Promise<RTCIceServer[]> {
  if (iceCache && iceCache.until > Date.now()) return iceCache.servers;
  try {
    const r = await get<{ iceServers: RTCIceServer[]; expiresAt: string }>("/api/calls/ice");
    iceCache = { servers: r.iceServers, until: Date.parse(r.expiresAt) - 5 * 60_000 };
    return r.iceServers;
  } catch {
    return [];
  }
}

function cleanup() {
  if (reconnectTimer) clearTimeout(reconnectTimer);
  reconnectTimer = null;
  pc?.close();
  pc = null;
  local?.getTracks().forEach((t) => t.stop());
  local = null;
  if (remoteAudio) {
    remoteAudio.srcObject = null;
    remoteAudio.remove();
    remoteAudio = null;
  }
  pendingCandidates = [];
  remoteSet = false;
}

async function buildPeer(callId: string) {
  const conn = new RTCPeerConnection({ iceServers: await iceServers() });
  pc = conn;
  local?.getTracks().forEach((t) => conn.addTrack(t, local!));
  conn.onicecandidate = (e) => {
    if (state?.callId === callId) send({ t: "call.signal", callId, data: { kind: "candidate", candidate: e.candidate ? e.candidate.toJSON() : null } });
  };
  conn.ontrack = (e) => {
    if (!remoteAudio) {
      remoteAudio = document.createElement("audio");
      remoteAudio.autoplay = true;
      document.body.appendChild(remoteAudio);
    }
    remoteAudio.srcObject = e.streams[0] ?? new MediaStream([e.track]);
    void remoteAudio.play().catch(() => {});
  };
  conn.onconnectionstatechange = () => {
    const s = conn.connectionState;
    if (state?.callId !== callId) return;
    if (s === "connected") {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectTimer = null;
      set({ phase: "active", error: null, startedAt: state.startedAt ?? Date.now() });
    } else if (s === "disconnected") {
      set({ phase: "reconnecting" });
      if (reconnectTimer) clearTimeout(reconnectTimer);
      // Give the network a few seconds to recover, then try an ICE restart from the caller side.
      reconnectTimer = setTimeout(() => void restartIce(callId), 5000);
    } else if (s === "failed") {
      endLocal(callId, "failed");
    }
  };
  return conn;
}

async function restartIce(callId: string) {
  if (!pc || state?.callId !== callId || state.role !== "caller") return;
  if (pc.connectionState === "connected") return;
  try {
    const offer = await pc.createOffer({ iceRestart: true });
    await pc.setLocalDescription(offer);
    send({ t: "call.signal", callId, data: { kind: "offer", sdp: offer.sdp! } });
  } catch {
    endLocal(callId, "failed");
  }
}

/** Ends the call on the server and clears local state. */
function endLocal(callId: string, reason: CallEndReason) {
  if (state?.callId !== callId) return;
  send({ t: "call.end", callId });
  finish(reason);
}

function finish(reason: CallEndReason) {
  cleanup();
  if (!state) return;
  set({ phase: "ended", endReason: reason, muted: false, peerMuted: false });
  const id = state.callId;
  setTimeout(() => {
    if (state?.callId === id && state.phase === "ended") set(null);
  }, 4000);
}

/** Start a call to someone. Acquires the microphone first so permission problems show before ringing. */
export async function startCall(peer: CallPeer): Promise<string | null> {
  if (state && state.phase !== "ended") return "already_in_call";
  try {
    local = await getMicrophone();
  } catch (e: any) {
    return e.code;
  }
  const callId = uuid();
  set({ callId, role: "caller", peer, phase: "calling", muted: false, peerMuted: false, startedAt: null, endReason: null, error: null });
  send({ t: "call.invite", to: peer.id, callId });
  return null;
}

/** Accept an incoming call. Microphone permission is requested now, when the user chooses to answer. */
export async function acceptCall() {
  if (!state || state.phase !== "incoming") return;
  const callId = state.callId;
  try {
    local = await getMicrophone();
  } catch (e: any) {
    set({ error: e.code });
    send({ t: "call.decline", callId });
    finish("failed");
    return;
  }
  set({ phase: "connecting" });
  send({ t: "call.accept", callId });
}

export function declineCall() {
  if (!state) return;
  if (state.phase === "incoming") send({ t: "call.decline", callId: state.callId });
  else send({ t: "call.end", callId: state.callId });
  finish(state.phase === "incoming" ? "declined" : "ended");
}

export function hangUp() {
  if (!state) return;
  const callId = state.callId;
  const reason: CallEndReason = state.phase === "calling" || state.phase === "ringing" ? "cancelled" : "ended";
  send({ t: "call.end", callId });
  finish(reason);
}

export function toggleMute() {
  if (!state || !local) return;
  const muted = !state.muted;
  local.getAudioTracks().forEach((t) => (t.enabled = !muted));
  set({ muted });
  send({ t: "call.mute", callId: state.callId, muted });
}

/** Server messages that drive the call. Returns true when the message belonged to a call. */
export async function handleCallMessage(m: ServerMsg): Promise<boolean> {
  switch (m.t) {
    case "call.incoming": {
      if (state && state.phase !== "ended") {
        send({ t: "call.decline", callId: m.callId }); // already busy with another call
        return true;
      }
      set({ callId: m.callId, role: "callee", peer: m.from, phase: "incoming", muted: false, peerMuted: false, startedAt: null, endReason: null, error: null });
      return true;
    }
    case "call.state": {
      if (!state || state.callId !== m.callId) return true; // snapshot for a call this tab does not hold
      if (m.state === "ringing") {
        set({ phase: "ringing", peer: m.peer });
      } else if (m.state === "active") {
        set({ peer: m.peer, peerMuted: m.peerMuted, phase: state.phase === "reconnecting" ? "reconnecting" : "connecting" });
        if (m.role === "caller" && local) {
          // The caller makes the offer. If a connection already exists (after a socket reconnect), restart ICE instead.
          if (pc && pc.connectionState !== "closed") {
            await restartIce(m.callId);
          } else {
            const conn = await buildPeer(m.callId);
            const offer = await conn.createOffer();
            await conn.setLocalDescription(offer);
            send({ t: "call.signal", callId: m.callId, data: { kind: "offer", sdp: offer.sdp! } });
          }
        }
      }
      return true;
    }
    case "call.signal": {
      if (!state || state.callId !== m.callId) return true;
      await applySignal(m.callId, m.data);
      return true;
    }
    case "call.mute": {
      if (state?.callId === m.callId) set({ peerMuted: m.muted });
      return true;
    }
    case "call.ended": {
      if (state?.callId !== m.callId) return true;
      finish(m.reason);
      return true;
    }
    case "error": {
      if (!state || m.ref !== state.callId || state.phase === "ended") return false;
      // The server refused something about this call (for example, the other person is busy).
      set({ error: m.code });
      if (state.phase === "calling") finish("failed");
      return true;
    }
    default:
      return false;
  }
}

async function applySignal(callId: string, data: CallSignal) {
  if (data.kind === "offer" || data.kind === "answer") {
    if (!pc) {
      if (data.kind !== "offer" || !local) return;
      await buildPeer(callId);
    }
    const conn = pc!;
    await conn.setRemoteDescription({ type: data.kind, sdp: data.sdp });
    remoteSet = true;
    for (const c of pendingCandidates.splice(0)) await conn.addIceCandidate(c).catch(() => {});
    if (data.kind === "offer") {
      const answer = await conn.createAnswer();
      await conn.setLocalDescription(answer);
      send({ t: "call.signal", callId, data: { kind: "answer", sdp: answer.sdp! } });
    }
    return;
  }
  if (data.kind === "candidate" && data.candidate) {
    if (!pc || !remoteSet) pendingCandidates.push(data.candidate as RTCIceCandidateInit);
    else await pc.addIceCandidate(data.candidate as RTCIceCandidateInit).catch(() => {});
  }
}

/** Keep call state in sync with the socket. */
let wired = false;
export function wireCalls() {
  if (wired) return;
  wired = true;
  subscribe((m) => {
    void handleCallMessage(m);
  });
}

/** Human text for a call phase. */
export function phaseLabel(c: CallState, t: Record<string, string>): string {
  switch (c.phase) {
    case "calling": return t.callCalling;
    case "ringing": return t.callRinging;
    case "incoming": return t.incomingCall;
    case "connecting": return t.callConnecting;
    case "reconnecting": return t.callReconnecting;
    case "active": return t.callActive;
    case "ended":
      return ({ declined: t.callDeclined, missed: t.callMissed, cancelled: t.callCancelled, failed: t.callFailed, connection_lost: t.callFailed, busy: t.callFailed } as Record<string, string>)[c.endReason ?? ""] ?? t.callEnded;
  }
}
